import { useEffect, useRef, useCallback } from "react";
import { Switch, Route, Redirect, useLocation, Router as WouterRouter } from "wouter";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { performInactivityLogout } from "@/lib/inactivityLogout";
import { useInactivityTimeout } from "@/hooks/useInactivityTimeout";
import { useInactivityPreference } from "@/hooks/useInactivityPreference";
import { useSessionExpiryStorageSweep } from "@/hooks/useSessionExpiryStorageSweep";
import { PlanProvider } from "@/contexts/PlanContext";
import { ThemeProvider } from "@/components/theme-provider";
import { TourPlayerProvider } from "@/components/tour-player-context";
import { ElementPickerProvider } from "@/components/element-picker-context";
import Layout from "@/components/layout";
import { PageErrorBoundary } from "@/components/page-error-boundary";
import Dashboard from "@/pages/dashboard";
import GoalsPage from "@/pages/goals";
import GoalDetailPage from "@/pages/goal-detail";
import VboardPage from "@/pages/vboard";
import HabitsPage from "@/pages/habits";
import TasksPage from "@/pages/tasks";
import JournalPage from "@/pages/journal";
import NotepadPage from "@/pages/notepad";
import PlannerPage from "@/pages/planner";
import IntentionsPage from "@/pages/intentions";
import GratefulsPage from "@/pages/gratefuls";
import IncantationsPage from "@/pages/incantations";
import ChecklistsPage from "@/pages/checklists";
import Media from "@/pages/media";
import DrivePage from "@/pages/drive";
import CalendarPage from "@/pages/calendar";
import AnalyticsPage from "@/pages/analytics";
import AchievementsPage from "@/pages/achievements";
import ScorecardPage from "@/pages/scorecard";
import SettingsPage from "@/pages/settings";
import ZeusSettingsPage from "@/pages/zeus-settings";
import VaultPage from "@/pages/vault";
import UserManagement from "@/pages/user-management";
import UserProfile from "@/pages/user-profile";
import Landing from "@/pages/landing";
import Pricing from "@/pages/pricing";
import PublicPricing from "@/pages/public-pricing";
import Subscribe from "@/pages/subscribe";
import ResellerPage from "@/pages/reseller";
import LandingPagesPage from "@/pages/landing-pages";
import LandingPageBuilder from "@/pages/landing-page-builder";
import SampleLanding from "@/pages/sample-landing";
import AcceptInvitePage from "@/pages/accept-invite";
import AcceptFriendInvitePage from "@/pages/accept-friend-invite";
import AcceptAssistantInvitePage from "@/pages/accept-assistant-invite";
import ResetPasswordPage from "@/pages/reset-password";
import MeditationPage from "@/pages/meditation";
import MeditationSettingsPage from "@/pages/meditation-settings";
import MealPlannerPage from "@/pages/meal-planner";
import WorkoutPlannerPage from "@/pages/workout-planner";
import HealthMetricsPage from "@/pages/health-metrics";
import FoodGoalPage from "@/pages/food-goal";
import FitnessGoalPage from "@/pages/fitness-goal";
import SuggestionsPage from "@/pages/suggestions";
import AdminSuggestionsPage from "@/pages/admin-suggestions";
import AdminPanelPage, { MasterAdminPage } from "@/pages/admin-panel";
import AdminSimplePage from "@/pages/admin-simple";
import AdminSopsPage from "@/pages/admin-sops";
import SopsPage from "@/pages/sops";
import TestimonialsPage from "@/pages/testimonials";
import AdminTestimonialsPage from "@/pages/admin-testimonials";
import AdminTestimonialEditPage from "@/pages/admin-testimonial-edit";
import ManageTenantsPage from "@/pages/manage-tenants";
import AdminTenantsPage from "@/pages/admin-tenants";
import ApiKeysPage from "@/pages/api-keys";
import BillingPage from "@/pages/billing";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { PlanLimitDialog } from "@/components/plan-limit-dialog";
import AdminMigrationPage from "@/pages/admin-migration";
import PlatformBillingSettingsPage from "@/pages/platform-billing-settings";
import AdminToursPage from "@/pages/admin-tours";
import ToursPage from "@/pages/tours";
import TourPlayer from "@/pages/tour-player";
import ThemeBuilder from "@/pages/theme-builder";
import NotificationSettingsPage from "@/pages/notification-settings";
import AssistantsPage, { CoachPage } from "@/pages/assistants";
import AssistantSopsPage from "@/pages/assistant-sops";
import CommunicatePage from "@/pages/communicate";
import { ChatProvider, useChatContext } from "@/contexts/chat-context";
import { FloatingChat, ChatReturnBanner } from "@/components/messaging/floating-chat";
import ContactsPage from "@/pages/contacts";
import TimeEntriesPage from "@/pages/time-entries";
import TimeReportsPage from "@/pages/time-reports";
import AICoachSettingsPage from "@/pages/ai-coach-settings";
import AICoachConstraintsPage from "@/pages/ai-coach-constraints";
import AICoachReportingPage from "@/pages/ai-coach-reporting";
import AICoachStartPage from "@/pages/ai-coach-start";
import AICoachAskPage from "@/pages/ai-coach-ask";
import AICreatePage from "@/pages/ai-create";
import GoalSimulationPage from "@/pages/goal-simulation";
import TimeBudgetPage from "@/pages/time-budget";
import PlatformRolesPage from "@/pages/platform-roles";
import TrackerXPage from "@/pages/trackerx";
import ZeusPage from "@/pages/zeus";
import NotFound from "@/pages/not-found";
import SignupPage from "@/pages/signup";
import TrialExpiredPage from "@/pages/trial-expired";
import { queryClient, apiRequest } from "./lib/queryClient";
import { getQueryFn } from "@/lib/queryClient";
import TwoFactorPage, { twoFactorRoute } from "@/pages/two-factor";
import { assistantCanAccessPath, getRequiredSectionNameForPath, type AssistantCapability } from "@/lib/menu-items";
import { TaskChecklistWorkspaceProvider } from "@/components/checklists/task-checklist-workspace";
import { FeatureFlagGuard } from "@/components/feature-flag-guard";

const GoalsConstraintsPage = () => <AICoachConstraintsPage dataSource="goals" />;
const AffirmationsConstraintsPage = () => <AICoachConstraintsPage dataSource="affirmations" />;
const GratefulsConstraintsPage = () => <AICoachConstraintsPage dataSource="gratefuls" />;
const HabitsConstraintsPage = () => <AICoachConstraintsPage dataSource="habits" />;
const TasksConstraintsPage = () => <AICoachConstraintsPage dataSource="tasks" />;
const JournalConstraintsPage = () => <AICoachConstraintsPage dataSource="journal" />;
const CalendarConstraintsPage = () => <AICoachConstraintsPage dataSource="calendar" />;

function Router() {
  const { isAuthenticated, isLoading, hasConnectionTrouble, user, tenant } = useAuth();
  const [location, setLocation] = useLocation();
  const { data: twoFactorStatus } = useQuery<{ required: boolean; mode: "setup" | "challenge"; verified: boolean } | null>({
    queryKey: ["/api/auth/2fa/status"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && user?.requiresTwoFactor === true,
    retry: false,
  });
  const { data: effectivePermissions } = useQuery<{ permissions: Partial<Record<AssistantCapability, boolean>> } | null>({
    queryKey: ["/api/assistants/effective-permissions"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && user?.requiresTwoFactor === true && twoFactorStatus?.verified !== false,
    retry: false,
  });
  const { data: assistantProfile } = useQuery<{ id: string } | null>({
    queryKey: ["/api/assistants/profile"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated && user?.requiresTwoFactor === true && twoFactorStatus?.verified !== false,
    retry: false,
  });

  // Read user preference for inactivity timeout so users can control the
  // behaviour. The hook never arms the timer until the real preference has
  // loaded (fail-safe against transient fetch failures).
  const { userPrefs, inactivityEnabled, inactivityMinutes } = useInactivityPreference(isAuthenticated);

  // Keep the server's copy of the user's IANA timezone current so day
  // boundaries (e.g. recurring checklist resets) follow the USER's midnight,
  // not the server's. Synced once per session when it drifts.
  const tzSyncedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !userPrefs || tzSyncedRef.current) return;
    let deviceTz: string | undefined;
    try {
      deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return;
    }
    if (!deviceTz || userPrefs.timezone === deviceTz) return;
    tzSyncedRef.current = true;
    apiRequest("PATCH", "/api/user/preferences", { timezone: deviceTz }).catch(() => {
      // Best-effort: retried next session.
    });
  }, [isAuthenticated, userPrefs]);

  // Inactivity logout — honours the user's own on/off toggle and duration.
  const handleInactivityLogout = useCallback(() => {
    performInactivityLogout(isAuthenticated, user?.id);
  }, [isAuthenticated, user?.id]);
  useInactivityTimeout(handleInactivityLogout, inactivityEnabled, inactivityMinutes);

  // Clear user-scoped localStorage when a session expires so that a subsequent
  // user on the same browser cannot observe the previous user's UI state.
  // Guaranteed to fire exactly once per expiry even if the auth query refires.
  useSessionExpiryStorageSweep(user, isLoading);

  // Password reset takes precedence over two-factor enrollment.
  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.needsPasswordReset && location !== "/reset-password") {
      setLocation("/reset-password");
    }
  }, [isLoading, isAuthenticated, user?.needsPasswordReset, location, setLocation]);

  // Redirect to trial-expired page when the trial period has ended
  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      tenant?.subscriptionStatus === "trialing" &&
      tenant?.trialEndsAt &&
      new Date(tenant.trialEndsAt) < new Date() &&
      location !== "/trial-expired" &&
      location !== "/pricing" &&
      location !== "/subscribe"
    ) {
      setLocation("/trial-expired");
    }
  }, [isLoading, isAuthenticated, tenant, location, setLocation]);

  // Check for affiliate referral code in URL and track signup.
  // The ref code is read once on mount. If a code is present the visitor is unauthenticated
  // (the server rejects authenticated callers), so we store it in their session via the
  // public endpoint. We keep a flag so the follow-up signup call is only made when a code
  // was actually seen on this page load, preventing attribution for ordinary returning logins.
  const pendingReferral = useRef(false);
  // Keep a stable ref to `user` so the referral fetch callback can read current auth state
  // without being a stale closure — handles the race where auth resolves before the fetch.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');

    if (!refCode) return;

    fetch(`/api/track-referral/${encodeURIComponent(refCode)}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          // Remove ref param from URL without reload
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('ref');
          window.history.replaceState({}, '', newUrl.toString());

          if (userRef.current) {
            // Race condition: auth already resolved before the referral fetch finished —
            // fire the signup tracking immediately.
            fetch('/api/track-affiliate-signup', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            }).catch(console.error);
          } else {
            // Normal path: user will sign up/log in after this; the [user] effect below
            // will pick it up.
            pendingReferral.current = true;
          }
        }
      })
      .catch(console.error);
  }, []);

  // Link affiliate referral to the account that just signed up.
  // Only fires when both: (a) a referral code was present on this page load and the
  // referral fetch resolved before auth (normal signup flow), and (b) the user becomes
  // authenticated — i.e. they registered in this same session.
  useEffect(() => {
    if (user && pendingReferral.current) {
      pendingReferral.current = false;
      fetch('/api/track-affiliate-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }).catch(console.error);
    }
  }, [user]);

  // Show loading spinner while auth is being checked to prevent 404 flash
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center gap-4 bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        {hasConnectionTrouble && (
          <p className="text-sm text-muted-foreground" data-testid="text-connection-trouble">
            Having trouble connecting — retrying…
          </p>
        )}
      </div>
    );
  }
  if (isAuthenticated && user?.requiresTwoFactor === true && twoFactorStatus === undefined) {
    return <div className="min-h-screen w-full grid place-items-center bg-background"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  // An assistant must never briefly receive an unrestricted route while its
  // delegated permission document is loading.
  if (isAuthenticated && user?.requiresTwoFactor === true && assistantProfile === undefined && twoFactorStatus?.verified !== false) {
    return <div className="min-h-screen w-full grid place-items-center bg-background"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  const pendingTwoFactor = isAuthenticated && !user?.needsPasswordReset && twoFactorRoute(twoFactorStatus);
  const assistantDenied = !!assistantProfile && (!!effectivePermissions ? !assistantCanAccessPath(location, effectivePermissions.permissions) : true);
  // Paths (and their sub-paths) that assistants can always access regardless of
  // capability grants — account management pages they need even without a grant.
  // Only auth/lifecycle paths are always accessible to assistants.
  // "/settings" is intentionally excluded — assistants are capability-gated on all
  // application pages, including settings, and must receive the access-denied screen there.
  const publicAssistantPaths = ["/reset-password", "/two-factor", "/trial-expired"];

  return (
    <Switch>
      {/* Public routes */}
      <Route path="/signup" component={SignupPage} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/publicpricing" component={PublicPricing} />
      <Route path="/subscribe" component={Subscribe} />
      <Route path="/sample" component={SampleLanding} />
      <Route path="/accept-invite" component={AcceptInvitePage} />
      <Route path="/accept-friend-invite" component={AcceptFriendInvitePage} />
      <Route path="/accept-assistant-invite" component={AcceptAssistantInvitePage} />
      <Route path="/admin/panel" component={AdminPanelPage} />
      <Route path="/admin/simple" component={AdminSimplePage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
       <Route path="/two-factor" component={TwoFactorPage} />
      
      {/* Protected routes - show landing if not authenticated */}
       {!isAuthenticated ? (
        <>
          <Route path="/" component={Landing} />
          {/* Redirect any other path to landing when not authenticated */}
          <Route component={Landing} />
        </>
       ) : pendingTwoFactor ? (
         <Route component={TwoFactorPage} />
       ) : assistantDenied && !publicAssistantPaths.some(p => location === p || location.startsWith(`${p}/`)) ? (
         <Layout><main className="p-6"><h1 className="text-2xl font-semibold">Access denied{getRequiredSectionNameForPath(location) ? ` — ${getRequiredSectionNameForPath(location)}` : ""}</h1><p className="mt-2 text-muted-foreground">{getRequiredSectionNameForPath(location) ? `Ask your manager to enable ${getRequiredSectionNameForPath(location)} access from the Assistants settings.` : "Your assistant account has not been granted access to this area."}</p></main></Layout>
       ) : (
        <Layout>
          <Route path="/" component={Dashboard} />
          <Route path="/ai-create">{() => <FeatureFlagGuard flagKey="aiCreate"><AICreatePage /></FeatureFlagGuard>}</Route>
          <Route path="/goal-simulation" component={GoalSimulationPage} />
          <Route path="/time-budget">{() => <FeatureFlagGuard flagKey="timeBudget"><TimeBudgetPage /></FeatureFlagGuard>}</Route>
          <Route path="/zeus" component={ZeusPage} />
          <Route path="/manus">{() => <Redirect to="/zeus" />}</Route>
          <Route path="/trackerx">{() => <FeatureFlagGuard flagKey="trackerx"><TrackerXPage /></FeatureFlagGuard>}</Route>
          <Route path="/goals/:id" component={GoalDetailPage} />
          <Route path="/goals">{() => <PageErrorBoundary pageName="Goals"><GoalsPage /></PageErrorBoundary>}</Route>
          <Route path="/vboard">{() => <FeatureFlagGuard flagKey="visionBoard"><PageErrorBoundary pageName="Vboard"><VboardPage /></PageErrorBoundary></FeatureFlagGuard>}</Route>
          <Route path="/habits" component={HabitsPage} />
          <Route path="/tasks">{() => <PageErrorBoundary pageName="Tasks"><TasksPage key={user?.id} /></PageErrorBoundary>}</Route>
          <Route path="/journal" component={JournalPage} />
          <Route path="/journal/analytics" component={JournalPage} />
          <Route path="/journal/search" component={JournalPage} />
          <Route path="/notepad" component={NotepadPage} />
          <Route path="/planner">{() => <FeatureFlagGuard flagKey="planner"><PlannerPage /></FeatureFlagGuard>}</Route>
          <Route path="/meal-planner">{() => <FeatureFlagGuard flagKey="mealPlanner"><MealPlannerPage /></FeatureFlagGuard>}</Route>
          <Route path="/workout-planner">{() => <FeatureFlagGuard flagKey="workoutPlanner"><WorkoutPlannerPage /></FeatureFlagGuard>}</Route>
          <Route path="/health-metrics">{() => <FeatureFlagGuard flagKey="healthMetrics"><HealthMetricsPage /></FeatureFlagGuard>}</Route>
          <Route path="/food-goal">{() => <FeatureFlagGuard flagKey="healthMetrics"><FoodGoalPage /></FeatureFlagGuard>}</Route>
          <Route path="/fitness-goal">{() => <FeatureFlagGuard flagKey="healthMetrics"><FitnessGoalPage /></FeatureFlagGuard>}</Route>
          <Route path="/intentions" component={IntentionsPage} />
          <Route path="/gratefuls">{() => <FeatureFlagGuard flagKey="gratefuls"><GratefulsPage /></FeatureFlagGuard>}</Route>
          <Route path="/incantations" component={IncantationsPage} />
          <Route path="/checklists" component={ChecklistsPage} />
          <Route path="/checklists/completed" component={ChecklistsPage} />
          <Route path="/media">{() => <FeatureFlagGuard flagKey="media"><Media /></FeatureFlagGuard>}</Route>
          <Route path="/drive">{() => <FeatureFlagGuard flagKey="drive"><DrivePage /></FeatureFlagGuard>}</Route>
          <Route path="/community">{() => <Redirect to="/communicate" />}</Route>
          <Route path="/calendar" component={CalendarPage} />
          <Route path="/analytics" component={AnalyticsPage} />
          <Route path="/achievements">{() => <FeatureFlagGuard flagKey="achievements"><AchievementsPage /></FeatureFlagGuard>}</Route>
          <Route path="/scorecard" component={ScorecardPage} />
          <Route path="/settings" component={SettingsPage} />
          <Route path="/zeus-settings" component={ZeusSettingsPage} />
          <Route path="/vault" component={VaultPage} />
          <Route path="/user-management" component={UserManagement} />
          <Route path="/user-profile/:id" component={UserProfile} />
          <Route path="/reseller" component={ResellerPage} />
          <Route path="/landing-pages" component={LandingPagesPage} />
          <Route path="/landing-page-builder" component={LandingPageBuilder} />
          <Route path="/meditation">{() => <FeatureFlagGuard flagKey="meditation"><MeditationPage /></FeatureFlagGuard>}</Route>
          <Route path="/meditation/settings">{() => <FeatureFlagGuard flagKey="meditation"><MeditationSettingsPage /></FeatureFlagGuard>}</Route>
          <Route path="/suggestions">{() => <FeatureFlagGuard flagKey="suggestions"><SuggestionsPage /></FeatureFlagGuard>}</Route>
          <Route path="/admin/suggestions" component={AdminSuggestionsPage} />
          <Route path="/admin/panel" component={AdminPanelPage} />
          <Route path="/master-admin" component={MasterAdminPage} />
          <Route path="/testimonials" component={TestimonialsPage} />
          <Route path="/admin/testimonials" component={AdminTestimonialsPage} />
          <Route path="/admin/testimonials/:id/edit" component={AdminTestimonialEditPage} />
          <Route path="/admin/tenants" component={AdminTenantsPage} />
           <Route path="/admin/platform-roles" component={PlatformRolesPage} />
          <Route path="/admin/manage-tenants" component={ManageTenantsPage} />
          <Route path="/manage-tenants" component={ManageTenantsPage} />
          <Route path="/api-keys" component={ApiKeysPage} />
          <Route path="/billing" component={BillingPage} />
          <Route path="/admin/migration" component={AdminMigrationPage} />
          <Route path="/admin/platform-billing" component={PlatformBillingSettingsPage} />
          <Route path="/admin/tours" component={AdminToursPage} />
          <Route path="/admin/sops" component={AdminSopsPage} />
          <Route path="/sops" component={SopsPage} />
          <Route path="/admin/theme" component={ThemeBuilder} />
          <Route path="/notification-settings" component={NotificationSettingsPage} />
          <Route path="/assistants/sops" component={AssistantSopsPage} />
          <Route path="/communicate" component={CommunicatePage} />
          <Route path="/assistants">{() => <AssistantsPage />}</Route>
          <Route path="/coach" component={CoachPage} />
          <Route path="/contacts" component={ContactsPage} />
          <Route path="/time-entries" component={TimeEntriesPage} />
          <Route path="/time-reports" component={TimeReportsPage} />
          <Route path="/tours" component={ToursPage} />
          <Route path="/ai-coach/start">{() => <FeatureFlagGuard flagKey="aiCoach"><AICoachStartPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/ask">{() => <FeatureFlagGuard flagKey="aiCoach"><AICoachAskPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/settings">{() => <FeatureFlagGuard flagKey="aiCoach"><AICoachSettingsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/constraints/goals">{() => <FeatureFlagGuard flagKey="aiCoach"><GoalsConstraintsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/constraints/affirmations">{() => <FeatureFlagGuard flagKey="aiCoach"><AffirmationsConstraintsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/constraints/gratefuls">{() => <FeatureFlagGuard flagKey="aiCoach"><GratefulsConstraintsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/constraints/habits">{() => <FeatureFlagGuard flagKey="aiCoach"><HabitsConstraintsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/constraints/tasks">{() => <FeatureFlagGuard flagKey="aiCoach"><TasksConstraintsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/constraints/journal">{() => <FeatureFlagGuard flagKey="aiCoach"><JournalConstraintsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/constraints/calendar">{() => <FeatureFlagGuard flagKey="aiCoach"><CalendarConstraintsPage /></FeatureFlagGuard>}</Route>
          <Route path="/ai-coach/reporting">{() => <FeatureFlagGuard flagKey="aiCoach"><AICoachReportingPage /></FeatureFlagGuard>}</Route>
        </Layout>
      )}
      
      {/* Trial expired — shown when trial ends, no layout chrome needed */}
      {isAuthenticated && <Route path="/trial-expired" component={TrialExpiredPage} />}

      {/* Tour player - rendered outside layout for full-screen overlay */}
      {isAuthenticated && <Route path="/tour/:id" component={TourPlayer} />}
      
      <Route component={NotFound} />
    </Switch>
  );
}

/** Shows a return-to-conversation banner when chat context link was followed */
function ChatReturnBannerRenderer() {
  const { floatingChat, setReturnThread } = useChatContext();
  const rt = floatingChat.returnThread;
  if (!rt) return null;
  return <ChatReturnBanner userId={rt.userId} userName={rt.userName} onDismiss={() => setReturnThread(null)} />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <ThemeProvider>
          <PlanProvider>
            <TooltipProvider>
              <TourPlayerProvider>
                <ElementPickerProvider>
                  <ChatProvider>
                   <TaskChecklistWorkspaceProvider>
                    <Toaster />
                    <ImpersonationBanner />
                    <PlanLimitDialog />
                    <Router />
                    <FloatingChat />
                    <ChatReturnBannerRenderer />
                   </TaskChecklistWorkspaceProvider>
                  </ChatProvider>
                </ElementPickerProvider>
              </TourPlayerProvider>
            </TooltipProvider>
          </PlanProvider>
        </ThemeProvider>
      </WouterRouter>
    </QueryClientProvider>
  );
}

export default App;
